import axios, {
	AxiosRequestConfig,
	AxiosInstance,
	CreateAxiosDefaults,
	AxiosError,
	InternalAxiosRequestConfig,
} from 'axios';
import {
	createDPoPRequestInterceptor,
	createDPoPResponseInterceptor,
} from '@phonepe/axios-dpop-interceptor';
import { IDPoPApiClientConfig } from '../types';

export class ApiDpopClient {
	private axiosInstance: AxiosInstance;

	constructor(config: IDPoPApiClientConfig) {
		this.axiosInstance = axios.create(config);
		if (!config.hasCustomInterceptor) {
			this.setupDPoPInterceptor(config.statusCodes, config.refreshToken);
		}
	}

	setupDPoPInterceptor = (
		statusCodes: Array<number> = [],
		refreshToken: (instance: AxiosInstance) => Promise<void> = () => Promise.resolve(),
	) => {
		createDPoPRequestInterceptor(this.axiosInstance);
		createDPoPResponseInterceptor(
			this.axiosInstance,
			() => refreshToken?.(this.axiosInstance),
			{
				statusCodes: [...(statusCodes ? statusCodes : [])],
			},
		);
	};

	getAxiosInstance = (): AxiosInstance => {
		return this.axiosInstance;
	};

	static getCancelController = () => {
		const abortController = new AbortController();
		return {
			signal: abortController.signal,
			cancel: () => abortController.abort(),
		};
	};

	addRequestInterceptors(
		resolveInterceptor: (
			config: InternalAxiosRequestConfig,
		) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
		rejectInterceptor?: (error: AxiosError) => Promise<AxiosError>,
	) {
		this.axiosInstance.interceptors.request.use(
			resolveInterceptor,
			rejectInterceptor,
		);
	}

	addResponseInterceptors(
		resolveInterceptor:
			| ((
					value: axios.AxiosResponse<unknown, unknown>,
			  ) =>
					| axios.AxiosResponse<unknown, unknown>
					| Promise<axios.AxiosResponse<unknown, unknown>>)
			| undefined,
		rejectInterceptor: ((error: unknown) => unknown) | undefined,
	) {
		this.axiosInstance.interceptors.response.use(
			resolveInterceptor,
			rejectInterceptor,
		);
	}

	async get(
		url: string,
		headers: Record<string, string> = {},
		params = {},
		config: AxiosRequestConfig = {},
	) {
		const requestConfig = {
			method: 'GET',
			url,
			headers,
			params,
			...config,
		};
		return await this.axiosInstance.request(requestConfig);
	}

	async post(
		url: string,
		data: unknown = {},
		headers: Record<string, string> = {},
		config: AxiosRequestConfig = {},
	) {
		const requestConfig = {
			method: 'POST',
			url,
			data,
			headers,
			...config,
		};
		return await this.axiosInstance.request(requestConfig);
	}

	async delete(url: string, config: AxiosRequestConfig = {}) {
		const requestConfig = {
			method: 'DELETE',
			url,
			...config,
		};
		return await this.axiosInstance.request(requestConfig);
	}

	async patch(
		url: string,
		data: unknown = {},
		headers: Record<string, string> = {},
		config: AxiosRequestConfig = {},
	) {
		const requestConfig = {
			method: 'PATCH',
			url,
			data,
			headers,
			...config,
		};
		return await this.axiosInstance.request(requestConfig);
	}

	async put(
		url: string,
		data: unknown = {},
		headers: Record<string, string> = {},
		config: AxiosRequestConfig = {},
	) {
		const requestConfig = {
			method: 'PUT',
			url,
			data,
			headers,
			...config,
		};
		return await this.axiosInstance.request(requestConfig);
	}
}
